import { Fragment, useState } from 'react';

import { RadioGroup } from '@nesso/ui/primitives/radio-group';
import { RadioGroupItemField } from '@nesso/ui/primitives/radio-group-item-field';

import {
    managingSymptomsCategorySchema,
    type ProgressEnum,
    type ProgressInput,
    progressSchema,
} from '@/entities/session-note';
import { slugify } from '@/shared/lib/format-utils';
import { CommentableCheckbox } from '@/shared/ui/checkbox-list/checkbox-list-with-other/commentable-checkbox';
import { ToggleableViewCheckboxList } from '@/shared/ui/checkbox-list/toggleable-view-checkbox-list';
import { NoteTextarea } from '@/shared/ui/common/note-textarea';
import { FieldSection } from '@/shared/ui/forms/field-section';

import { NessoSymptomGroupNode } from './nesso-progress-section/nesso-symptom-group-node';

type OverallProgress = ProgressEnum | 'other';
type ProgressContent = ProgressInput;

const OVERALL_PROGRESS_OPTIONS: { value: OverallProgress; label: string }[] = [
    ...progressSchema.shape.progress.unwrap().options.map((label) => ({ value: label, label })),
    { value: 'other' as const, label: 'Other' },
];

const IMPROVEMENT_OPTIONS = progressSchema.shape.improvement
    .unwrap()
    .element.options.map((label) => ({ value: label, label }));

const SYMPTOM_GROUPS = managingSymptomsCategorySchema.options.map((variant) => ({
    group: variant.shape.symptomCategory.value,
    options: variant.shape.symptoms
        .unwrap()
        .element.options.map((label) => ({ value: label, label })),
}));

const STRUGGLE_OPTIONS = progressSchema.shape.continuesToStruggle
    .unwrap()
    .element.options.map((label) => ({ value: label, label }));

const IMPACTS_TO_DAILY_LIFE_OPTIONS = progressSchema.shape.impactsToDailyLife
    .unwrap()
    .element.options.map((label) => ({ value: label, label }));

type ManagingSymptom = NonNullable<ProgressContent['managingSymptoms']>[number];

type Props = {
    content: ProgressContent;
    onChange: (content: ProgressContent) => void;
};

export function NessoProgressSection({ content, onChange }: Props) {
    const [justSelectedOverall, setJustSelectedOverall] = useState<OverallProgress | null>(null);
    const [managingSymptomsOtherOpen, setManagingSymptomsOtherOpen] = useState(
        content.managingSymptomsOther !== undefined,
    );

    const overall: OverallProgress | '' =
        content.progressOther !== undefined ? 'other' : (content.progress ?? '');

    const handleOverallChange = (next: OverallProgress) => {
        setJustSelectedOverall(next);
        if (next === 'other') {
            onChange({
                ...content,
                progress: undefined,
                progressOther: content.progressOther ?? '',
            });
        } else {
            onChange({
                ...content,
                progress: next,
                progressOther: undefined,
            });
        }
    };

    const findSymptom = (category: string) =>
        content.managingSymptoms?.find((s) => s.symptomCategory === category);

    const patchSymptom = (category: string, patch: { symptoms?: string[]; other?: string }) => {
        const entries = content.managingSymptoms ?? [];
        const idx = entries.findIndex((s) => s.symptomCategory === category);
        const next: ManagingSymptom[] =
            idx >= 0
                ? entries.map((e, i) => (i === idx ? ({ ...e, ...patch } as ManagingSymptom) : e))
                : [...entries, { symptomCategory: category, ...patch } as ManagingSymptom];
        onChange({ ...content, managingSymptoms: next });
    };

    const toggleSymptom = (category: string, active: boolean) => {
        const entries = content.managingSymptoms ?? [];
        if (active) {
            if (entries.some((s) => s.symptomCategory === category)) return;
            onChange({
                ...content,
                managingSymptoms: [...entries, { symptomCategory: category } as ManagingSymptom],
            });
        } else {
            onChange({
                ...content,
                managingSymptoms: entries.filter((s) => s.symptomCategory !== category),
            });
        }
    };

    return (
        <FieldSection title="Progress" className="gap-9">
            <FieldSection label="In general, client is..." className="gap-3">
                <RadioGroup<OverallProgress | ''>
                    value={overall}
                    onValueChange={(value) => {
                        // '' is only the group's nothing-selected value; no item emits it.
                        if (value !== '') handleOverallChange(value);
                    }}
                >
                    {OVERALL_PROGRESS_OPTIONS.map((option) => {
                        const id = `overall-${slugify(option.value)}`;
                        return (
                            <Fragment key={option.value}>
                                <RadioGroupItemField
                                    id={id}
                                    value={option.value}
                                    label={option.label}
                                    fieldProps={{ className: 'gap-3' }}
                                />
                                {option.value === 'other' && overall === 'other' && (
                                    <div className="pl-6">
                                        <NoteTextarea
                                            autoFocus={justSelectedOverall === 'other'}
                                            value={content.progressOther ?? ''}
                                            onChange={(progressOther) =>
                                                onChange({ ...content, progressOther })
                                            }
                                            className="w-full"
                                        />
                                    </div>
                                )}
                            </Fragment>
                        );
                    })}
                </RadioGroup>
            </FieldSection>

            <FieldSection label="Client shows improvement in..." className="gap-4">
                <ToggleableViewCheckboxList
                    options={IMPROVEMENT_OPTIONS}
                    values={content.improvement ?? []}
                    columns={1}
                    onValuesChange={(improvement) => onChange({ ...content, improvement })}
                    otherValue={content.improvementOther}
                    onOtherValueChange={(improvementOther) =>
                        onChange({ ...content, improvementOther })
                    }
                />
            </FieldSection>
            <FieldSection label="Client shows progress managing symptoms of..." className="gap-4">
                <div className="flex flex-col gap-1">
                    {SYMPTOM_GROUPS.map((group) => {
                        const entry = findSymptom(group.group);
                        const active = !!entry;
                        const selection = entry?.symptoms ?? [];
                        const other = entry?.other;
                        return (
                            <NessoSymptomGroupNode
                                key={group.group}
                                label={group.group}
                                options={group.options}
                                selection={selection}
                                other={other}
                                active={active}
                                onToggle={(checked) => toggleSymptom(group.group, checked)}
                                onSelectionChange={(next) =>
                                    patchSymptom(group.group, { symptoms: next })
                                }
                                onOtherChange={(next) => patchSymptom(group.group, { other: next })}
                            />
                        );
                    })}
                    <CommentableCheckbox
                        label="Other"
                        checked={managingSymptomsOtherOpen}
                        onCheckedChange={setManagingSymptomsOtherOpen}
                        comment={content.managingSymptomsOther}
                        onCommentChange={(managingSymptomsOther) =>
                            onChange({ ...content, managingSymptomsOther })
                        }
                    />
                </div>
            </FieldSection>

            <FieldSection label="Client continues to struggle with..." className="gap-4">
                <ToggleableViewCheckboxList
                    columns={1}
                    options={STRUGGLE_OPTIONS}
                    values={content.continuesToStruggle ?? []}
                    onValuesChange={(continuesToStruggle) =>
                        onChange({ ...content, continuesToStruggle })
                    }
                    otherValue={content.continuesToStruggleOther}
                    onOtherValueChange={(continuesToStruggleOther) =>
                        onChange({ ...content, continuesToStruggleOther })
                    }
                />
            </FieldSection>

            <FieldSection label="Impact to daily life:" className="gap-4 mb-3">
                <ToggleableViewCheckboxList
                    columns={1}
                    options={IMPACTS_TO_DAILY_LIFE_OPTIONS}
                    values={content.impactsToDailyLife ?? []}
                    onValuesChange={(impactsToDailyLife) =>
                        onChange({ ...content, impactsToDailyLife })
                    }
                    otherValue={content.impactsToDailyLifeOther}
                    onOtherValueChange={(impactsToDailyLifeOther) =>
                        onChange({ ...content, impactsToDailyLifeOther })
                    }
                />
            </FieldSection>

            <FieldSection label="New barriers to progress" className="gap-3">
                <NoteTextarea
                    value={content.barriersToProgress}
                    onChange={(barriersToProgress) => onChange({ ...content, barriersToProgress })}
                    rows={3}
                />
            </FieldSection>
        </FieldSection>
    );
}
